<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { useI18n } from 'vue-i18n';
import {
    House,
    Globe,
    Layers,
    Users,
    Landmark,
    Settings,
    Search,
    ArrowRight,
} from 'lucide-vue-next';
import {
    Dialog,
    DialogContent,
    DialogTitle,
} from '@/components/ui/dialog';
import { dashboard as creatorDashboard, members as creatorMembers, payouts as creatorPayouts } from '@/routes/creator';
import { index as creatorPlans } from '@/routes/creator/plans';
import { edit as creatorProfile } from '@/routes/creator/profile';

const { t } = useI18n();
const open = ref(false);
const query = ref('');
const selectedIndex = ref(0);
const inputRef = ref<HTMLInputElement | null>(null);

type CommandItem = {
    id: string;
    label: string;
    href: string;
    icon: typeof House;
    group: string;
};

const allItems = computed<CommandItem[]>(() => [
    { id: 'dashboard', label: t('backend.sidebar.dashboard'), href: creatorDashboard.url(), icon: House, group: t('backend.topbar.navigation') },
    { id: 'page', label: t('backend.sidebar.myPage'), href: creatorProfile.url(), icon: Globe, group: t('backend.topbar.navigation') },
    { id: 'plans', label: t('backend.sidebar.plans'), href: creatorPlans.url(), icon: Layers, group: t('backend.topbar.navigation') },
    { id: 'supporters', label: t('backend.sidebar.supporters'), href: creatorMembers.url(), icon: Users, group: t('backend.topbar.navigation') },
    { id: 'payouts', label: t('backend.sidebar.payouts'), href: creatorPayouts.url(), icon: Landmark, group: t('backend.topbar.navigation') },
    { id: 'settings', label: t('backend.sidebar.settings'), href: '/app/settings/profile', icon: Settings, group: t('backend.topbar.navigation') },
]);

const filtered = computed(() => {
    if (!query.value) return allItems.value;
    const q = query.value.toLowerCase();
    return allItems.value.filter((item) => item.label.toLowerCase().includes(q));
});

const groups = computed(() => {
    const map = new Map<string, CommandItem[]>();
    for (const item of filtered.value) {
        const group = map.get(item.group) ?? [];
        group.push(item);
        map.set(item.group, group);
    }
    return map;
});

watch(query, () => {
    selectedIndex.value = 0;
});

watch(open, (val) => {
    if (val) {
        query.value = '';
        selectedIndex.value = 0;
    }
});

function navigate(item: CommandItem) {
    open.value = false;
    router.visit(item.href);
}

function onKeydown(e: KeyboardEvent) {
    if (e.key === 'ArrowDown') {
        e.preventDefault();
        selectedIndex.value = Math.min(selectedIndex.value + 1, filtered.value.length - 1);
    } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        selectedIndex.value = Math.max(selectedIndex.value - 1, 0);
    } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = filtered.value[selectedIndex.value];
        if (item) navigate(item);
    }
}

function onGlobalKeydown(e: KeyboardEvent) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        open.value = !open.value;
    }
}

onMounted(() => {
    document.addEventListener('keydown', onGlobalKeydown);
});

onUnmounted(() => {
    document.removeEventListener('keydown', onGlobalKeydown);
});

defineExpose({ open });
</script>

<template>
    <!-- Trigger: search bar -->
    <button
        class="flex h-[30px] w-[280px] items-center gap-1.5 rounded-md border border-border bg-card px-2.5 text-[13px] text-left"
        @click="open = true"
    >
        <Search class="size-4 shrink-0 text-muted-foreground" :stroke-width="1.6" />
        <span class="flex-1 text-muted-foreground">{{ t('backend.topbar.search') }}</span>
        <span class="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">⌘K</span>
    </button>

    <!-- Dialog -->
    <Dialog v-model:open="open">
        <DialogContent
            :show-close-button="false"
            class="!max-w-[520px] !gap-0 overflow-hidden !p-0"
        >
            <DialogTitle class="sr-only">{{ t('backend.topbar.commandPalette') }}</DialogTitle>

            <!-- Search input -->
            <div class="flex items-center gap-2.5 border-b border-border px-4 py-3">
                <Search class="size-4 shrink-0 text-muted-foreground" :stroke-width="1.6" />
                <input
                    ref="inputRef"
                    v-model="query"
                    :placeholder="t('backend.topbar.typeCommand')"
                    class="h-6 flex-1 border-0 bg-transparent text-[14px] text-foreground outline-none placeholder:text-muted-foreground"
                    autofocus
                    @keydown="onKeydown"
                />
                <kbd class="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">ESC</kbd>
            </div>

            <!-- Results -->
            <div class="max-h-[320px] overflow-y-auto py-2">
                <template v-if="filtered.length === 0">
                    <div class="px-4 py-8 text-center text-[13px] text-muted-foreground">
                        {{ t('backend.topbar.noResults') }}
                    </div>
                </template>
                <template v-for="[group, items] in groups" :key="group">
                    <div class="px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                        {{ group }}
                    </div>
                    <button
                        v-for="(item, i) in items"
                        :key="item.id"
                        class="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] transition-colors"
                        :class="filtered.indexOf(item) === selectedIndex
                            ? 'bg-muted text-foreground'
                            : 'text-nv-ink2 hover:bg-muted'"
                        @click="navigate(item)"
                        @mouseenter="selectedIndex = filtered.indexOf(item)"
                    >
                        <component :is="item.icon" class="size-4 shrink-0 text-muted-foreground" :stroke-width="1.6" />
                        <span class="flex-1">{{ item.label }}</span>
                        <ArrowRight v-if="filtered.indexOf(item) === selectedIndex" class="size-3.5 text-muted-foreground" :stroke-width="1.6" />
                    </button>
                </template>
            </div>
        </DialogContent>
    </Dialog>
</template>
